import { App } from '@capacitor/app';
import { AppLauncher } from '@capacitor/app-launcher';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { SplashScreen } from '@capacitor/splash-screen';
import Framework7, { Router } from 'framework7/lite/bundle';
import { Event, recordAnalytic, recordEvent } from './analytics.ts';
import { AppRater } from './app-rater.ts';
import { APP_VERSION, DEV_MODE } from './env.ts';
import translate, { getLanguage } from './i18n.ts';
import { ANDROID_STORE_URL, EMAIL_ADDRESS, IOS_STORE_URL, INSTALL_URL as SHARE_APP_URL, WEBSITE_SUPPORTED_LANG, WEBSITE_URL } from './media.ts';
import { ROUTE_REPORT_ISSUE, ROUTE_SETTINGS_ABOUT_CHANGES } from './routes.ts';
import { APP_NAME, checkAppWasUpdated, incrementStartCount, isFirstStart } from './settings.ts';
import { showThankYouDialog } from './utils.ts';

/* eslint-disable @typescript-eslint/no-explicit-any */
export function logError(message: string, error?: Error | any) {
    let stack;
    if (error) {
        message += `: ${JSON.stringify(error)}`;
        stack = error?.stack;
    } else {
        stack = new Error().stack; // create a stack
    }
    console.error(message + '\n' + stack);
    recordAnalytic(Event.Error, { 'description': message });
}

// TODO handle Promise
export function runAndLogErrors(
    func: () => void,
    message: string = `Error while calling function ${func.name || 'anonymous'}`) {
    try {
        func();
    } catch (err) {
        logError(message, err);
    }
}

export interface DeviceInfo {
    platform: string;
    native: boolean;
    ios: boolean;
    android: boolean;
    /* not native, browser/web/PWA */
    web: boolean;
    /** PWA installed on device, standalone mode */
    pwa: boolean;
    /** mobile browser / desktop */
    mobile: boolean;
}

export interface AppInfo {
    version: string,
    build: string,
}


function getDeviceInfo() {
    const nativePlatform = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();

    const isWeb = platform === 'web';
    // check PWA screen modes
    const pwaScreenMatch = ["fullscreen", "standalone", "minimal-ui"].some(
        (displayMode) => window?.matchMedia && window.matchMedia('(display-mode: ' + displayMode + ')').matches
    );
    // check navigator
    const standalonePwa = ('standalone' in window.navigator) && (window?.navigator?.standalone != null);
    const isPwa = isWeb && (pwaScreenMatch || standalonePwa);

    // check using window size, not perfect
    const isMobile = window.innerWidth < 768;

    const deviceInfo: DeviceInfo = {
        platform: platform,
        native: nativePlatform,
        ios: platform === 'ios',
        android: platform === 'android',
        web: isWeb,
        pwa: isPwa,
        mobile: isMobile,
    }

    return deviceInfo
}

/**
 * Contains information about the current device. Can be called anytime during the app lifecycle.
 * 
 * The `device` object is populated by the `getDeviceInfo()` function and may include properties
 * such as device type, operating system, browser, and other relevant details. 
 * 
 */
export const device = getDeviceInfo();

export const isDevMode = DEV_MODE;

export async function minimizeApp() {
    if (device.native) await App.minimizeApp(); // only no native devices
}

export const removeHtml = (html: string, replacement: string = '') => {
    return html.replace(/<[^>]*>/g, replacement);
}

export const rateApp = (f7: Framework7) => {
    new AppRater().showReviewAppDialog(f7); // ask if app liked and ask to review
};

export const shareAppAndThank = (f7: Framework7) => {
    shareApp().then(() => showThankYouDialog(f7));
};

export async function shareApp() {
    const canshare = await Share.canShare();
    if (!canshare.value) {
        logError('navigator.share is not available');
        return;
    }

    try {
        const shareRslt = await Share.share({
            title: `${APP_NAME}, ${translate('about.slogan')}`,
            text: removeHtml(translate('about.description')), // TODO name user
            url: createLangUrl(SHARE_APP_URL),
            dialogTitle: translate(`${translate("common.share")} ${APP_NAME}`),
        });
        // analytics
        recordEvent(Event.Share, { content_type: "app", method: shareRslt.activityType });

    } catch (err) {
        logError('Error sharing', err)
    }
}

let f7app: Framework7 | null = null;

export const getFramework = () => {
    if (!f7app) throw new Error('Application not initialized yet');
    return f7app;
};

// show errors in the UI
export const showToast = (message: string) => {
    f7app?.toast.create({
        text: message,
        closeTimeout: 3000,
        closeButton: true,
    }).open(); // TODO: add a report issue link
}


/**
 * log the error in console.error(), record an error analytics event 
 * and show in the UI 
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const logAndShowError = (message: string, error?: Error | any) => {
    const getDisplayError = (error?: Error | any) => {
        if (!error) return '';
        if (error instanceof Error) return error.message;
        if (typeof error === 'string') return error;
        if (typeof error === 'object' && error.message) return String(error.message);
        return String(error);
    };

    logError(message, error); // console.error and report analytics
    const errorText = getDisplayError(error);
    const displayMessage = errorText ? `${message}: ${errorText}` : message;
    // show in UI
    showErrorToast(displayMessage);
}

export const showErrorToast = (message: string) => {
    f7app?.toast.create({
        text: message,
        closeTimeout: 5000,
        closeButton: true,
        closeButtonText: 'Report Issue',
        closeButtonColor: 'red',
        destroyOnClose: true,
        on: {
            closeButtonClick: function () {
                getRouter()?.navigate(ROUTE_REPORT_ISSUE, {
                    history: false,
                    clearPreviousHistory: true // will force to go back home afterwards
                });
            },
        }
    }).open();
}

/**
 * Init the app instance
 * 
 * @param f7app 
 */
export function initApp(f7: Framework7) {
    f7app = f7; // save f7 app instance

    // replace default handler
    window.onerror = function (message, url, linenumber) {
        logAndShowError(`Error: ${message}\nLine ${linenumber} for ${url}`);
    }
    // listen to promise rejections
    window.addEventListener("unhandledrejection", (event) => {
        logAndShowError(`Error: ${event.reason}`);
    });

    // useful for PWA deployments when refresh is needed
    // see https://vitejs.dev/guide/build#load-error-handling
    window.addEventListener('vite:preloadError', () => {
        logError('Caught vite:preloadError, reloading page');
        window.location.reload() // refresh the page
    })

    // increment app start count
    incrementStartCount();
}

export async function getAppInfo() {
    const nativePlatform = Capacitor.isNativePlatform();
    let version = APP_VERSION;
    let build = 'unavailable';

    // get device app information
    if (nativePlatform) {
        const appInfo = await App.getInfo();
        version = appInfo.version
        build = appInfo.build
    }

    const appInfo: AppInfo = {
        version: version,
        build: build,
    }

    return appInfo
}


export const createLangUrl = (url: string): string => {
    if (!url.startsWith(WEBSITE_URL)) {
        logError(`url:${url} should start with ${WEBSITE_URL}`);
        return url;
    }
    const supportedLngs = WEBSITE_SUPPORTED_LANG;
    const curLang = getLanguage();
    if (supportedLngs.includes(curLang)) { // website has the translation
        const relativeUrl = url.replace(WEBSITE_URL, '');
        return `${WEBSITE_URL}/${curLang}${relativeUrl}`
    }
    return url;
}

export async function browseWebsite(relativeUrl: string) {
    const supportedLngs = WEBSITE_SUPPORTED_LANG;
    const curLang = getLanguage();
    let url = WEBSITE_URL;
    if (supportedLngs.includes(curLang)) { // website has the translation
        url = `${url}/${curLang}${relativeUrl}`
    } else { // default to english
        url = `${url}${relativeUrl}`
    }
    await browseURL(url);
}

export async function browseURL(url: string) {
    recordEvent(Event.PageView, { page_location: url })
    if (device.native && url.startsWith('mailto')) {
        await AppLauncher.openUrl({ url: url }); // on IOS and Android open the mailto app
    } else {
        await Browser.open({ url: url });
    }
}

export async function openEmail(toaddr = EMAIL_ADDRESS, subject = '', message = '') {
    let url = "mailto:" + toaddr;
    const params = [];
    if (subject) {
        params.push(`subject=${encodeURIComponent(subject)}`);
    }
    if (message) {
        // Replace standard newlines with the %0D%0A format for iOS/Android compatibility
        params.push(`body=${encodeURIComponent(message.replace(/\n/g, "\r\n"))}`);
    }
    if (params.length) {
        for (let index = 0; index < params.length; index++) {
            if (index == 0)
                url += '?';
            else
                url += '&';
            url += params[index];
        }
    }

    await browseURL(url);
}


export function showInstallToast(f7: Framework7): Framework7.Toast | null {
    // show only on web browsers on device app can be installed
    if (!device.web) return;
    const userAgent = navigator.userAgent.toLowerCase();

    // Checks if should display install popup notification:
    let installUrl = null;
    if (userAgent.match(/(iphone|ipad)/)) {
        if (device.pwa)
            return; // app already added to home screen as pwa
        installUrl = IOS_STORE_URL; // TODO IOS URL
    } else if (userAgent.match(/android/)) {
        installUrl = ANDROID_STORE_URL;
    }
    if (!installUrl) return; // not on a web brower where the app can be installed

    const toast = f7.toast.create({
        text: `<a class='external link' href="${installUrl}">${translate('home.install_now')}</a>`,
        closeTimeout: 30 * 1000,
        closeButton: true,
        closeButtonText: '✕',
    });
    toast.open();
    return toast;
}

export async function checkForUpdateToast(f7: Framework7): Framework7.Toast | null {
    const appInfo = await getAppInfo();
    const updateInfo = checkAppWasUpdated(appInfo.version);

    if (!updateInfo.updated) return; // no updates detected
    if (isFirstStart()) return;

    console.info("Detected version update", updateInfo);
    const toast = f7.toast.create({
        text: `${translate('home.app_was_updated')} 🚀 <a class='link' href="${ROUTE_SETTINGS_ABOUT_CHANGES}">${translate('home.see_new_features')}</a>`,
        closeTimeout: 10 * 1000,
        closeButton: true,
        closeButtonText: '✕',
    });
    toast.open();
    return toast;
}

export class ToastScheduler {
    private timer?: number;
    private toast?: Toast.Toast;

    constructor() { }

    public schedule(delay: number, func: () => Toast.Toast) {
        this.timer = window.setTimeout(async () => {
            this.toast = await func();
        }, delay);
    }

    public stop() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = undefined;
        }
        if (this.toast) {
            this.toast.close();
            this.toast = undefined;
        }
    }
}

export async function closeSplashScreen() {
    if (!device.native) {
        return;
    }
    await SplashScreen.hide();
    console.debug("Closed splash screen");
}
export const getRouter = (): Router.Router | undefined => f7app?.views?.main?.router;


export const goBackOrGoHome = (f7router?: Router.Router): void => {
    if (!f7router) {
        f7router = getRouter();
        if (!f7router) return;
    }
    // router would have ['/', '/url1'] in history
    // we want to redirect straight to '/' instead of relying 
    // on browser history that can redirect outside the whole app
    if (f7router.history && f7router.history.length <= 2) {
        f7router.navigate("/", {
            reloadCurrent: true, // update the browserurl with the current route
            animate: true,
            clearPreviousHistory: true,
        });
    } else {
        f7router.back();
    }
};
